import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FilterChip, PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import {
  ToolbarSortSelection,
} from '@shared/components/table-toolbar/table-toolbar.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { DataTableMultiSelectFilterComponent, DataTableMultiSelectFilterOption } from '@shared/components/data-table-multi-select-filter/data-table-multi-select-filter.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { NotificationService } from '@shared/services/notification.service';
import {
  DeleteConfirmDialogComponent,
  DeleteConfirmDialogData,
} from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import {
  CommunityHistoryEditDialogComponent,
  CommunityHistoryEditDialogResult,
} from '../components/community-history-edit-dialog.component';
import {
  CommunityTransferDateDialogComponent,
  CommunityTransferDateDialogResult,
} from '../components/community-transfer-date-dialog.component';
import {
  CommunityTransferDialogComponent,
  CommunityTransferDialogResult,
} from '../components/community-transfer-dialog.component';
import { Person } from '../models/person.model';
import { PersonCommunityHistoryItem } from '../models/person-community-history.model';
import { PersonService } from '../services/person.service';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { resolveCommunityHistoryCreateMode } from './community-history-create-mode';
import { synchronizeCommunityFilterSelection } from './community-history-filter';
import { PersonEventsContextService } from './person-events-shell-page/person-events-context.service';

type CommunityHistoryViewMode = 'table' | 'timeline';

@Component({
  selector: 'app-person-community-history-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TableSurfaceComponent,
    DataTableMultiSelectFilterComponent,
  ],
  templateUrl: './person-community-history-page.component.html',
  styleUrl: './person-community-history-page.component.scss',
})
export class PersonCommunityHistoryPageComponent implements OnInit, OnDestroy {
  @ViewChild(TableSurfaceComponent) tableSurfaceRef?: TableSurfaceComponent<PersonCommunityHistoryItem>;

  person: Person | null = null;
  history: PersonCommunityHistoryItem[] = [];
  filteredHistory: PersonCommunityHistoryItem[] = [];
  tableData: PersonCommunityHistoryItem[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  activeFilterChips: FilterChip[] = [];
  searchTerm = '';
  sortState: ToolbarSortSelection = { sortBy: 'validFrom', direction: 'desc' };
  communityFilterOptions: string[] = [];
  communitySelectOptions: DataTableMultiSelectFilterOption[] = [];
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  canManageMembers = false;
  viewMode: CommunityHistoryViewMode = 'table';
  readonly statusFilterOptions: DataTableMultiSelectFilterOption[] = [
    { value: 'current', label: 'Actual' },
    { value: 'finished', label: 'Finalizada' },
  ];
  private communityFilterInitialized = false;

  readonly filterForm = new FormGroup({
    status: new FormControl<string[]>(['current', 'finished'], { nonNullable: true }),
    community: new FormControl<string[]>([], { nonNullable: true }),
  });

  readonly allColumns: TableColumn[] = [
    { key: 'period', header: 'Periodo', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'duration', header: 'Tiempo', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'community', header: 'Comunidad', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'reason', header: 'Motivo', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'status', header: 'Estado', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'actions', header: '', width: '1%', minWidth: '104px', exportable: false },
  ];

  private personId = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly notifications: NotificationService,
    private readonly personService: PersonService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly userFullInfoService: UserFullInfoService,
    private readonly eventsContext: PersonEventsContextService,
  ) { }

  ngOnInit(): void {
    this.personId = this.route.snapshot.pathFromRoot
      .map(route => route.paramMap.get('person-id'))
      .find((personId): personId is string => !!personId) ?? '';
    this.filterForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyTableState();
        this.cdr.markForCheck();
      });
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageMembers = this.userFullInfoService.canManageMemberDataSnapshot(user);
        this.cdr.markForCheck();
      });
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  navigateToUnit(unitId: string): void {
    this.router.navigate(['/units', unitId]);
  }

  back(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  onCreate(): void {
    if (!this.canManageMembers) {
      return;
    }

    const current = this.history.find(item => item.isCurrent);
    if (!current?.validFrom) {
      if (current && this.canEdit(current)) {
        this.onEdit(current);
      }
      return;
    }

    this.dialog
      .open(CommunityTransferDateDialogComponent, modalDialogConfig({
        data: { initialDate: current.validFrom, minDate: this.person?.dateOfBirth ?? null },
        size: 'sm',
        layout: 'dense',
      }))
      .afterClosed()
      .pipe(filter((result): result is CommunityTransferDateDialogResult => !!result), takeUntil(this.destroy$))
      .subscribe(result => {
        const mode = resolveCommunityHistoryCreateMode(result.validFrom, current.validFrom!);
        if (mode === 'duplicate') {
          this.notifications.error('No puede haber dos comunidades con la misma fecha de inicio.');
          return;
        }

        if (mode === 'blocked') {
          this.notifications.error('Primero corregi la comunidad actual para registrar su fecha de ingreso.');
          return;
        }

        this.personService.validateCommunityTransferDate(this.personId, result.validFrom)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              if (mode === 'historical') {
                this.openHistoricalCreateDialog(result.validFrom);
                return;
              }

              this.openOperationalTransferDialog(result.validFrom);
            },
            error: err => {
              this.notifications.error(this.getErrorMessage(
                err,
                'No se puede registrar un pase con esa fecha.'
              ));
              this.cdr.markForCheck();
            },
          });
      });
  }

  setViewMode(mode: string): void {
    if (mode === 'table' || mode === 'timeline') {
      this.viewMode = mode;
    }
  }

  get currentSort(): ToolbarSortSelection {
    return this.sortState;
  }

  get columns(): TableColumn[] {
    return this.canManageMembers
      ? this.allColumns
      : this.allColumns.filter(column => column.key !== 'actions');
  }

  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: this.totalPages,
    };
  }

  get hasEditableHistory(): boolean {
    return this.history.some(item => this.canEdit(item) || this.canDelete(item));
  }

  canEdit(item: PersonCommunityHistoryItem): boolean {
    return this.canManageMembers && item.id >= 0;
  }

  canDelete(item: PersonCommunityHistoryItem): boolean {
    return this.canManageMembers && item.id > 0;
  }

  onToolbarSearch(term: string): void {
    this.searchTerm = term.trim();
    this.currentPage = 1;
    this.applyTableState();
    this.cdr.markForCheck();
  }

  onToolbarSort(event: ToolbarSortSelection): void {
    this.sortState = event;
    this.currentPage = 1;
    this.applyTableState();
    this.cdr.markForCheck();
  }

  onFilterRemove(key: string): void {
    if (key === 'search') {
      this.searchTerm = '';
      this.currentPage = 1;
      this.tableSurfaceRef?.clearSearch();
      this.applyTableState();
      this.cdr.markForCheck();
      return;
    }

    if (key === 'status') {
      this.currentPage = 1;
      this.filterForm.controls.status.setValue(this.statusFilterOptions.map(option => option.value));
    } else if (key === 'community') {
      this.currentPage = 1;
      this.filterForm.controls.community.setValue(this.communityFilterOptions);
    }
  }

  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.applyHistoryPage();
    this.cdr.markForCheck();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.applyHistoryPage();
    this.cdr.markForCheck();
  }

  onEdit(item: PersonCommunityHistoryItem): void {
    if (!this.canEdit(item)) {
      return;
    }

    this.dialog
      .open(CommunityHistoryEditDialogComponent, modalDialogConfig({
        data: { item, mode: 'edit', minDate: this.person?.dateOfBirth ?? null },
        size: 'sm',
        layout: 'form',
      }))
      .afterClosed()
      .pipe(filter((result): result is CommunityHistoryEditDialogResult => !!result), takeUntil(this.destroy$))
      .subscribe(result => {
        this.personService.updateCommunityHistory(this.personId, item.id, {
          organizationUnitId: result.organizationUnitId,
          validFrom: result.validFrom,
          reason: result.reason,
        }).pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notifications.success('Historial corregido.');
              this.loadData();
            },
            error: err => {
              this.notifications.error(this.getErrorMessage(err, 'No se pudo corregir el historial.'));
              this.cdr.markForCheck();
            },
          });
      });
  }

  onDelete(item: PersonCommunityHistoryItem): void {
    if (!this.canDelete(item)) {
      return;
    }

    const data: DeleteConfirmDialogData = item.hasApprovedMinistryTransfer
      ? {
          title: 'Eliminar pase',
          content: `
            <p>Estas seguro de que queres eliminar el pase a <strong>${this.escapeHtml(item.organizationUnitName)}</strong>?</p>
            <p><strong>Atencion:</strong> este pase tiene un pase ministerial aprobado asociado. Al eliminarlo, el historial de comunidades se recalculara, pero el ministerio actual y el historial ministerial no se revertiran automaticamente.</p>
          `,
          confirmButtonText: 'Eliminar',
        }
      : {
          title: 'Eliminar pase',
          message: `Estas seguro de que queres eliminar el pase a "${item.organizationUnitName}"? El historial se recalculara automaticamente.`,
          confirmButtonText: 'Eliminar',
        };

    this.dialog
      .open(DeleteConfirmDialogComponent, modalDialogConfig({ data, size: 'sm', layout: 'confirm', tone: 'danger' }))
      .afterClosed()
      .pipe(filter(result => !!result), takeUntil(this.destroy$))
      .subscribe(() => {
        this.personService.deleteCommunityHistory(this.personId, item.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notifications.success('Pase eliminado.');
              this.loadData();
            },
            error: err => {
              this.notifications.error(this.getErrorMessage(err, 'No se pudo eliminar el pase.'));
              this.cdr.markForCheck();
            },
          });
      });
  }

  trackByHistory(_: number, item: PersonCommunityHistoryItem): string {
    return `${item.id}-${item.organizationUnitId}-${item.validFrom ?? 'current'}`;
  }

  formatPeriodStart(item: PersonCommunityHistoryItem): string {
    return item.validFrom ? this.formatDate(item.validFrom) : 'Sin fecha registrada';
  }

  formatPeriodEnd(item: PersonCommunityHistoryItem): string {
    return `hasta ${item.validTo ? this.formatDate(item.validTo) : 'actualidad'}`;
  }

  formatDuration(item: PersonCommunityHistoryItem): string {
    if (!item.validFrom) {
      return '-';
    }

    const start = this.parseIsoDate(item.validFrom);
    const endInclusive = item.validTo ? this.parseIsoDate(item.validTo) : new Date();
    const endExclusive = this.addDays(endInclusive, 1);
    const totalMonths = this.calculateRoundedMonths(start, endExclusive);

    if (totalMonths <= 0) {
      return 'menos de 1 mes';
    }

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const parts: string[] = [];

    if (years > 0) {
      parts.push(`${years} ${years === 1 ? 'año' : 'años'}`);
    }

    if (months > 0) {
      parts.push(`${months} ${months === 1 ? 'mes' : 'meses'}`);
    }

    return parts.join(' y ');
  }

  private loadData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    forkJoin({
      person: this.eventsContext.refreshPerson(),
      history: this.personService.getCommunityHistory(this.personId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ person, history }) => {
          this.person = person;
          this.breadcrumbService.setDynamicCrumb('unit-id', person.communityName);
          this.breadcrumbService.setDynamicCrumb('person-id', `${person.firstName} ${person.lastName}`);
          this.history = history;
          this.updateFilterOptions();
          this.applyTableState();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: err => {
          this.isLoading = false;
          this.errorMessage = this.getErrorMessage(err, 'No se pudo cargar el historial de comunidades.');
          this.cdr.markForCheck();
        },
      });
  }

  private applyTableState(): void {
    const filters = this.filterForm.getRawValue();
    const search = this.normalizeText(this.searchTerm);

    let result = this.history.filter(item => {
      if (filters.status.length === 0 || !filters.status.includes(item.isCurrent ? 'current' : 'finished')) {
        return false;
      }

      if (filters.community.length === 0 || !filters.community.includes(item.organizationUnitName)) {
        return false;
      }

      if (!search) {
        return true;
      }

      return this.getSearchText(item).includes(search);
    });

    result = this.sortHistory(result);
    this.filteredHistory = result;
    this.applyHistoryPage();
    this.buildFilterChips();
  }

  private applyHistoryPage(): void {
    this.totalCount = this.filteredHistory.length;
    this.totalPages = Math.ceil(this.totalCount / this.pageSize);

    if (this.totalPages > 0 && this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.tableData = this.filteredHistory.slice(start, end);
  }

  private updateFilterOptions(): void {
    const previousOptions = this.communityFilterOptions;
    const updatedOptions = this.uniqueSorted(
      this.history.map(item => item.organizationUnitName).filter(Boolean)
    );
    this.communityFilterOptions = updatedOptions;
    this.communitySelectOptions = updatedOptions.map(value => ({ value }));

    if (!this.communityFilterInitialized && updatedOptions.length > 0) {
      this.communityFilterInitialized = true;
      this.filterForm.controls.community.setValue(updatedOptions, { emitEvent: false });
      return;
    }

    const selectedValues = this.filterForm.controls.community.value;
    const updatedSelection = synchronizeCommunityFilterSelection(
      previousOptions,
      selectedValues,
      updatedOptions,
    );

    if (!this.haveSameValues(selectedValues, updatedSelection)) {
      this.filterForm.controls.community.setValue(updatedSelection, { emitEvent: false });
    }
  }

  private buildFilterChips(): void {
    const filters = this.filterForm.getRawValue();
    const chips: FilterChip[] = [];

    if (this.searchTerm) {
      chips.push({ key: 'search', label: `Busqueda: ${this.searchTerm}`, removable: true });
    }

    this.addMultiSelectChip(chips, 'status', 'Estado', filters.status, this.statusFilterOptions);
    this.addMultiSelectChip(chips, 'community', 'Comunidad', filters.community, this.communitySelectOptions);

    this.activeFilterChips = chips;
  }

  private addMultiSelectChip(
    chips: FilterChip[],
    key: string,
    label: string,
    values: string[],
    options: DataTableMultiSelectFilterOption[],
  ): void {
    if (values.length === options.length) {
      return;
    }

    const selection = values.length === 0
      ? 'Ninguno'
      : values.map(value => options.find(option => option.value === value)?.label ?? value).join(', ');
    chips.push({ key, label: `${label}: ${selection}`, removable: true });
  }

  private sortHistory(items: PersonCommunityHistoryItem[]): PersonCommunityHistoryItem[] {
    return [...items].sort((a, b) => {
      const valA = this.getSortValue(a, this.sortState.sortBy);
      const valB = this.getSortValue(b, this.sortState.sortBy);
      const result = typeof valA === 'number' && typeof valB === 'number'
        ? valA - valB
        : String(valA).localeCompare(String(valB), 'es', { sensitivity: 'base' });

      return this.sortState.direction === 'asc' ? result : -result;
    });
  }

  private getSortValue(item: PersonCommunityHistoryItem, sortBy: string): string | number {
    switch (sortBy) {
      case 'period':
      case 'validFrom':
        return item.validFrom ?? '';
      case 'duration':
        return item.validFrom ? this.calculateRoundedMonths(this.parseIsoDate(item.validFrom), this.addDays(item.validTo ? this.parseIsoDate(item.validTo) : new Date(), 1)) : -1;
      case 'community':
        return item.organizationUnitName ?? '';
      case 'reason':
        return item.reason ?? '';
      case 'status':
        return item.isCurrent ? 1 : 0;
      default:
        return '';
    }
  }

  private getSearchText(item: PersonCommunityHistoryItem): string {
    return this.normalizeText([
      this.formatPeriodStart(item),
      this.formatPeriodEnd(item),
      this.formatDuration(item),
      item.organizationUnitName,
      item.reason,
      item.isCurrent ? 'Actual' : 'Finalizada',
    ].filter(Boolean).join(' '));
  }

  private openHistoricalCreateDialog(initialDate: string): void {
    this.dialog
      .open(CommunityHistoryEditDialogComponent, modalDialogConfig({
        data: { mode: 'create', initialDate, lockDate: true, minDate: this.person?.dateOfBirth ?? null },
        size: 'sm',
        layout: 'form',
      }))
      .afterClosed()
      .pipe(filter((result): result is CommunityHistoryEditDialogResult => !!result), takeUntil(this.destroy$))
      .subscribe(result => {
        this.personService.createCommunityHistory(this.personId, {
          organizationUnitId: result.organizationUnitId,
          validFrom: result.validFrom,
          reason: result.reason,
        }).pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notifications.success('Pase historico agregado.');
              this.loadData();
            },
            error: err => {
              this.notifications.error(this.getErrorMessage(err, 'No se pudo agregar el pase historico.'));
              this.cdr.markForCheck();
            },
          });
      });
  }

  private openOperationalTransferDialog(initialDate: string): void {
    if (!this.person) {
      return;
    }

    this.dialog
      .open(CommunityTransferDialogComponent, modalDialogConfig({
        data: { person: this.person, initialDate, lockDate: true, minDate: this.person.dateOfBirth ?? null },
        size: 'lg',
        layout: 'form',
      }))
      .afterClosed()
      .pipe(filter((result): result is CommunityTransferDialogResult => !!result), takeUntil(this.destroy$))
      .subscribe(result => {
        this.personService.transferCommunity(this.personId, {
          targetUnitId: result.targetUnitId,
          effectiveDate: result.effectiveDate,
          reason: result.reason,
        }).pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notifications.success(`El pase a ${result.targetUnitName} fue registrado correctamente.`);
              this.loadData();
            },
            error: err => {
              this.notifications.error(this.getErrorMessage(err, 'No se pudo registrar el pase de comunidad.'));
              this.cdr.markForCheck();
            },
          });
      });
  }

  private getErrorMessage(err: unknown, fallback: string): string {
    if (!err || typeof err !== 'object') {
      return fallback;
    }

    const error = err as {
      error?: { detail?: string; title?: string };
      response?: string;
      detail?: string;
      message?: string;
    };

    if (error.error?.detail) return error.error.detail;
    if (error.detail) return error.detail;
    if (error.response) {
      try {
        const parsed = JSON.parse(error.response) as { detail?: string; title?: string };
        return parsed.detail ?? parsed.title ?? fallback;
      } catch {
        return fallback;
      }
    }
    if (error.error?.title) return error.error.title;
    if (error.message && error.message !== 'An unexpected server error occurred.') return error.message;
    return fallback;
  }

  private formatDate(value: string): string {
    const date = this.parseIsoDate(value);
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private normalizeText(value: string): string {
    return value
      .toLocaleLowerCase('es')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private uniqueSorted(values: string[]): string[] {
    return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }

  private haveSameValues(first: string[], second: string[]): boolean {
    return first.length === second.length && first.every((value, index) => value === second[index]);
  }

  private parseIsoDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    const originalDay = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
    return result;
  }

  private calculateRoundedMonths(start: Date, endExclusive: Date): number {
    if (endExclusive <= start) {
      return 0;
    }

    let completeMonths = (endExclusive.getFullYear() - start.getFullYear()) * 12
      + endExclusive.getMonth()
      - start.getMonth();

    if (this.addMonths(start, completeMonths) > endExclusive) {
      completeMonths--;
    }

    const anchor = this.addMonths(start, completeMonths);
    const nextAnchor = this.addMonths(start, completeMonths + 1);
    const elapsedAfterAnchor = endExclusive.getTime() - anchor.getTime();
    const monthSpan = nextAnchor.getTime() - anchor.getTime();

    return completeMonths + (elapsedAfterAnchor >= monthSpan / 2 ? 1 : 0);
  }
}
